import { computed, DestroyRef, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class HighlightingService implements OnDestroy {
  readonly #router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);

  // Signals to track highlighting state
  public highlightTarget = signal<string | null>(null);
  public achievementId = signal<string | null>(null);

  // Computed signal to check if any highlighting is active (for backdrop)
  public isActive = computed(() => !!this.highlightTarget());

  private highlightTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Automatically listen to router navigation events
    // Process current URL immediately
    this.checkCurrentUrl();

    // Subscribe to future navigation changes
    this.#router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.#router.parseUrl(this.#router.url).queryParams),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((queryParams) => {
        this.processQueryParams(queryParams);
      });
  }

  /**
   * Check current URL for query params (called on init and by components)
   */
  private checkCurrentUrl(): void {
    const urlTree = this.#router.parseUrl(this.#router.url);
    if (urlTree.queryParams['highlight'] && urlTree.queryParams['achievement']) {
      this.processQueryParams(urlTree.queryParams);
    }
  }

  /**
   * Initialize highlighting - can be called by components to ensure current URL is checked
   * This is useful for components that render after navigation completes
   */
  initializeHighlighting(): void {
    this.checkCurrentUrl();
  }

  /**
   * Process query parameters to set highlighting state
   */
  private processQueryParams(queryParams: Record<string, string | undefined>): void {
    const highlight = queryParams['highlight'] ?? null;
    const achievement = queryParams['achievement'] ?? null;

    if (highlight && achievement) {
      this.highlightTarget.set(highlight);
      this.achievementId.set(achievement);
      this.startHighlightTimeout();
    } else if (!highlight && !achievement && this.highlightTarget() !== null) {
      // Only clear if we had highlighting active and params are now gone
      this.clearHighlight();
    }
  }

  /**
   * Start the highlight timeout to automatically clear highlighting after 10 seconds
   */
  private startHighlightTimeout(): void {
    this.clearHighlightTimeout();
    this.highlightTimeout = setTimeout(() => {
      this.clearHighlight();
    }, 3000); // 3 seconds
  }

  /**
   * Clear the highlight timeout
   */
  private clearHighlightTimeout(): void {
    if (this.highlightTimeout) {
      clearTimeout(this.highlightTimeout);
      this.highlightTimeout = null;
    }
  }

  /**
   * Clear highlighting manually
   */
  clearHighlight(): void {
    this.highlightTarget.set(null);
    this.achievementId.set(null);
    this.clearHighlightTimeout();
  }

  /**
   * Manually set highlight
   */
  setHighlight(target: string, achievementId?: string): void {
    this.highlightTarget.set(target);
    if (achievementId) {
      this.achievementId.set(achievementId);
    }
    this.startHighlightTimeout();
  }

  /**
   * Check if a specific element is highlighted
   */
  isHighlighted(elementId: string): boolean {
    return this.highlightTarget() === elementId;
  }

  /**
   * Get CSS classes for highlighting
   */
  getHighlightClasses(elementId: string): string {
    return this.isHighlighted(elementId) ? 'highlight highlight-pulse' : '';
  }

  ngOnDestroy(): void {
    this.clearHighlight();
  }
}
